import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { colors, fonts, shadows } from "../theme";
import { COMPARE_METRICS, fmt, scoreScenarios, bestByMetric, exportBrief } from "../lib/scenarioBrief";
import { effectiveCircWeights } from "../lib/kbLogic";
import { useStudio } from "../store/useStudio";

// Side-by-side comparison of 2–4 saved scenarios with per-metric leaders,
// a weighted circularity score, and export of the winner as a client brief.
export default function ScenarioCompare({ scenarios, kb, onClose, onLoad }) {
  const circWeights = useStudio((s) => s.circWeights);
  const circLockEqual = useStudio((s) => s.circLockEqual);
  const scores = scoreScenarios(scenarios);
  const scoreById = Object.fromEntries(scores.map((s) => [s.id, s.score]));
  const best = bestByMetric(scenarios);
  const winnerIdx = scenarios.reduce((bi, s, i) => (scoreById[s.id] > scoreById[scenarios[bi].id] ? i : bi), 0);
  const winner = scenarios[winnerIdx];
  const [exported, setExported] = React.useState(false);

  const handleExport = () => {
    const w = winner.circ_weights && Object.keys(winner.circ_weights).length
      ? winner.circ_weights : effectiveCircWeights(circWeights, circLockEqual);
    const ok = exportBrief(winner, kb, scoreById[winner.id], { weights: w, locked: !!winner.circ_lock_equal });
    if (ok) setExported(true);
  };

  // Transparency: do the compared scenarios share the same loop weighting? If not, flag it.
  const weightKey = (s) => {
    if (s.circ_lock_equal) return "equal";
    const w = s.circ_weights || {};
    return ["water", "energy", "nutrient", "carbon"].map((k) => Math.round((w[k] || 0.25) * 100)).join("-");
  };
  const mixedWeights = new Set(scenarios.map(weightKey)).size > 1;

  return (
    <AnimatePresence>
      <motion.div
        style={styles.scrim}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="scenario-compare-modal"
      >
        <motion.div
          style={styles.panel}
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 18, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 220, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={styles.head}>
            <div>
              <div style={styles.eyebrow}>SCENARIO COMPARISON</div>
              <div style={styles.title}>{scenarios.length} configurations · weighted circularity score</div>
            </div>
            <button style={styles.closeBtn} onClick={onClose} data-testid="compare-close">×</button>
          </div>

          <div style={styles.winnerBanner} data-testid="compare-winner-banner">
            <span style={styles.winnerDot} />
            <span>
              <strong>{winner.name}</strong> leads with a circularity score of{" "}
              <strong>{scoreById[winner.id]}/100</strong>
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...styles.rowLabelCol }}>Metric</th>
                  {scenarios.map((s, i) => (
                    <th key={s.id} style={{ ...styles.th, ...(i === winnerIdx ? styles.thWinner : {}) }} data-testid={`compare-col-${s.id}`}>
                      <div style={styles.colName} title={s.name}>{s.name}</div>
                      <div style={styles.colMeta}>{s.onboarding?.location || "—"}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_METRICS.map((m) => (
                  <tr key={m.key}>
                    <td style={{ ...styles.td, ...styles.rowLabelCol }}>
                      <span style={{ ...styles.metricDot, background: m.accent }} />
                      {m.label}
                    </td>
                    {scenarios.map((s, i) => {
                      const v = Number(s.kpis?.[m.key]) || 0;
                      const lead = Number(scenarios[best[m.key]].kpis?.[m.key]) || 0;
                      const isBest = i === best[m.key];
                      const delta = v - lead;
                      return (
                        <td key={s.id} style={{ ...styles.td, ...(isBest ? styles.tdBest : {}) }} data-testid={`compare-${m.key}-${s.id}`}>
                          <span style={styles.cellVal}>
                            {fmt(v, m.d)}<span style={styles.cellUnit}>{m.unit}</span>
                          </span>
                          {isBest ? (
                            <span style={styles.leadTag}>LEADER</span>
                          ) : (
                            <span style={styles.deltaTag}>{delta >= 0 ? "+" : ""}{fmt(delta, m.d)}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td style={{ ...styles.td, ...styles.rowLabelCol, fontWeight: 700 }}>Overall Ranking</td>
                  {scenarios.map((s, i) => (
                    <td key={s.id} style={{ ...styles.td, ...(i === winnerIdx ? styles.tdBest : {}) }} data-testid={`compare-score-${s.id}`}>
                      <span style={styles.scoreVal}>{scoreById[s.id]}</span>
                      <span style={styles.cellUnit}>/100</span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ ...styles.td, ...styles.rowLabelCol }}>Circularity loop weights</td>
                  {scenarios.map((s) => {
                    const lk = s.circ_lock_equal;
                    const w = s.circ_weights || {};
                    const p = (k) => Math.round(((lk ? 0.25 : (w[k] ?? 0.25))) * 100);
                    return (
                      <td key={s.id} style={{ ...styles.td, fontFamily: "monospace", fontSize: 10.5, color: colors.textSecondary }} data-testid={`compare-weights-${s.id}`}>
                        {lk ? "equal (locked)" : `W${p("water")}·E${p("energy")}·N${p("nutrient")}·C${p("carbon")}`}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {mixedWeights && (
            <div style={styles.mixedNote} data-testid="compare-mixed-weights">
              ⚠ These scenarios were scored with <strong>different loop weights</strong> — their Circularity figures aren't a like-for-like comparison. Load each and set the same weights (or lock to equal) for a true apples-to-apples read.
            </div>
          )}

          <div style={styles.footer}>
            <div style={styles.hint}>Weighted: yield 30% · water 25% · energy 25% · circularity 20%</div>
            <div style={styles.actions}>
              {onLoad && (
                <button style={styles.ghostBtn} onClick={() => onLoad(winner)} data-testid="compare-load-winner">
                  Load winner into studio
                </button>
              )}
              <button style={styles.exportBtn} onClick={handleExport} data-testid="compare-export-brief">
                {exported ? "Re-export brief" : "Export winner as brief"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const styles = {
  scrim: { position: "fixed", inset: 0, background: "rgba(20,22,26,0.42)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  panel: { width: "min(920px, 96vw)", maxHeight: "88vh", overflowY: "auto", background: colors.surface, borderRadius: 24, border: `1px solid ${colors.border}`, boxShadow: shadows.float, padding: 28 },
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 },
  eyebrow: { fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.8, color: colors.textTertiary, fontWeight: 700 },
  title: { fontFamily: fonts.sans, fontSize: 17, fontWeight: 600, color: colors.textPrimary, marginTop: 4, letterSpacing: -0.3 },
  closeBtn: { width: 34, height: 34, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textSecondary, fontSize: 22, lineHeight: 1, cursor: "pointer", flexShrink: 0 },
  winnerBanner: { display: "flex", alignItems: "center", gap: 10, marginTop: 18, padding: "12px 16px", borderRadius: 14, background: "rgba(52,199,89,0.10)", border: "1px solid rgba(52,199,89,0.28)", fontFamily: fonts.sans, fontSize: 13.5, color: colors.textPrimary },
  winnerDot: { width: 9, height: 9, borderRadius: 999, background: colors.ok, flexShrink: 0, boxShadow: "0 0 0 4px rgba(52,199,89,0.18)" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, marginTop: 20 },
  th: { textAlign: "left", padding: "10px 14px", fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.borderStrong}`, verticalAlign: "top" },
  thWinner: { background: "rgba(52,199,89,0.06)", borderTopLeftRadius: 12, borderTopRightRadius: 12 },
  rowLabelCol: { minWidth: 150, color: colors.textPrimary },
  colName: { fontWeight: 700, color: colors.textPrimary, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  colMeta: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 0.3, color: colors.textTertiary, marginTop: 3, fontWeight: 400 },
  td: { padding: "12px 14px", fontFamily: fonts.sans, fontSize: 14, color: colors.textPrimary, borderBottom: `1px solid ${colors.border}`, whiteSpace: "nowrap" },
  tdBest: { background: "rgba(52,199,89,0.06)" },
  metricDot: { display: "inline-block", width: 8, height: 8, borderRadius: 999, marginRight: 8 },
  cellVal: { fontWeight: 700, fontSize: 17, letterSpacing: -0.4 },
  cellUnit: { fontSize: 10, color: colors.textTertiary, fontWeight: 500, marginLeft: 3 },
  scoreVal: { fontWeight: 700, fontSize: 20, letterSpacing: -0.6, color: colors.textPrimary },
  leadTag: { marginLeft: 8, fontFamily: fonts.mono, fontSize: 8.5, letterSpacing: 0.5, color: colors.ok, fontWeight: 700, verticalAlign: "middle" },
  deltaTag: { marginLeft: 8, fontFamily: fonts.mono, fontSize: 10, color: colors.textTertiary, verticalAlign: "middle" },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginTop: 22, flexWrap: "wrap" },
  hint: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 0.4, color: colors.textTertiary },
  mixedNote: { margin: "10px 0 0", padding: "9px 12px", borderRadius: 9, background: "rgba(224,138,30,0.1)", border: "1px solid rgba(224,138,30,0.3)", fontSize: 11.5, lineHeight: 1.5, color: "#8a5511" },
  actions: { display: "flex", gap: 10 },
  ghostBtn: { padding: "10px 18px", borderRadius: 999, border: `1px solid ${colors.borderStrong}`, background: "transparent", color: colors.textPrimary, fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  exportBtn: { padding: "10px 20px", borderRadius: 999, border: "none", background: colors.textPrimary, color: "#fff", fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, cursor: "pointer" },
};
